import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Select, SearchInput } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Translation } from "../api/types";

const LOCALES = [
  { tag: "en", label: "English" },
  { tag: "hi", label: "Hindi" },
  { tag: "zh", label: "Chinese" },
  { tag: "ar", label: "Arabic" },
  { tag: "bn", label: "Bengali" },
  { tag: "es", label: "Spanish" },
  { tag: "tr", label: "Turkish" },
  { tag: "th", label: "Thai" },
  { tag: "in", label: "Indonesian" },
  { tag: "fil", label: "Filipino" },
  { tag: "vi", label: "Vietnamese" },
  { tag: "en-NG", label: "English (Nigeria)" },
];

/**
 * Real CRUD against a real table (migration 0020) — not a placeholder.
 * The one thing this page is honest about, prominently, not buried: the
 * Android app currently bundles its 12 languages as static strings.xml
 * resources at build time, so a value edited here does not yet change what
 * the app displays. That's a real, separate Android-side integration
 * (fetching strings dynamically at startup) which hasn't been built —
 * stated plainly rather than implied otherwise.
 */
export function TranslationsPage() {
  const [locale, setLocale] = useState("en");
  const [search, setSearch] = useState("");
  const { rows, isLoading, error, reload } = usePagedList<Translation>("/admin/translations", {
    locale,
    key: search || undefined,
  });
  const [editing, setEditing] = useState<{ key: string; value: string; isNew: boolean } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Translation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.key || !editing.value) {
      showToast("Key and value are required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/translations/${encodeURIComponent(locale)}/${encodeURIComponent(editing.key)}`, {
        method: "PATCH",
        body: { value: editing.value },
      });
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save translation", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/translations/${encodeURIComponent(pendingDelete.locale)}/${encodeURIComponent(pendingDelete.key)}`, {
        method: "DELETE",
      });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<Translation>[] = [
    { header: "Key", render: (row) => <span className="font-mono text-xs">{row.key}</span> },
    { header: "Value", render: (row) => <span className="max-w-md text-ink">{row.value}</span> },
    { header: "Updated", render: (row) => new Date(row.updated_at).toLocaleDateString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing({ key: row.key, value: row.value, isNew: false })}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Translations">
      <div className="mb-4 rounded-lg border border-warn/30 bg-warn-bg px-4 py-3 text-xs text-warn">
        <strong>Not yet live in the app.</strong> The Android app currently bundles its languages as static resources
        at build time — editing a value here is real and saved, but won't change what users see until the app is
        updated to fetch strings from this table at runtime (a separate, real integration that hasn't been built yet).
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Select value={locale} onChange={(e) => setLocale(e.target.value)} className="w-auto">
          {LOCALES.map((l) => (
            <option key={l.tag} value={l.tag}>
              {l.label}
            </option>
          ))}
        </Select>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by key..." />
        <Button onClick={() => setEditing({ key: "", value: "", isNew: true })}>+ Add String</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => `${r.locale}:${r.key}`}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No translations for this language yet"
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.isNew ? `Add string for ${locale}` : `Edit ${editing?.key} (${locale})`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Key"
            value={editing?.key ?? ""}
            disabled={!editing?.isNew}
            onChange={(e) => setEditing((p) => (p ? { ...p, key: e.target.value } : p))}
            hint="Should match a string resource name used in the Android app, e.g. nav_home"
          />
          <Input label="Value" value={editing?.value ?? ""} onChange={(e) => setEditing((p) => (p ? { ...p, value: e.target.value } : p))} />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "${pendingDelete?.key}" (${pendingDelete?.locale})?`}
        description="This removes this translation entry."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
